<template>
  <b-container fluid>
    <BackButton />
    <MessageAlert>
      <article class="order" v-if="orderDetails">
        <div class="order-header">
          <b-row>
            <b-col lg="5" sm="7" class="pb-3">
              <div>Order # <b @click="reloadOrderDetails()">{{orderDetails.number}}</b></div>
              <div>Email <b>{{orderDetails.guest_email}}</b></div>
              <div v-if="orderDetails.date_shipped">Date Shipped <b>{{orderDetails.date_shipped.substring(0,10)}}</b></div>
              <div v-else>Date Placed <b>{{orderDetails.date_placed.substring(0,10)}}</b></div>

              <div v-if="flags.enableDiscountApplying || isStaffDashboard">
                Code <b>{{ voucherCodeToShow }}</b>
                <span @click="isAddVoucherFormShown = true" v-if="!hasVoucherDiscount">
                  <i class="fas fa-edit clickable"></i>
                </span>

                <span class="btn embedded-input-container" v-if="!hasVoucherDiscount && isAddVoucherFormShown">
                  <input class="embedded-input" v-model="voucherCode" @keyup.enter="applyDiscount" />
                  <span class="ok clickable" @click="applyDiscount">
                    <i class="fas fa-check-circle"></i>
                  </span>
                  <span class="cancel clickable" @click="hideVoucherForm">
                    <i class="fas fa-times-circle"></i>
                  </span>
                </span>
              </div>

              <div>Total <b>${{orderDetails.total_incl_tax.toFixed(2)}}</b></div>
            </b-col>
            <b-col lg="5" sm="5" class="pb-3">
              <dl>
                <dt>Ship To</dt>
                <dd><Address :value="orderDetails.shipping_address"/></dd>
              </dl>
            </b-col>
          </b-row>
          <b-row v-if="showButtons">
            <b-col md="6" class="order-actions mt-3 mt-md-0">
              <b-button v-if="orderDetails.needs_payment"
                @click="startPayment"
                variant="success"
                class="wide mb-3 mb-md-0">Pay &amp; Approve Order</b-button>
              <ActionButton v-else
                variant="success" action="approveOrder"
                class="wide"
                success="Order has been Approved">Approve Order</ActionButton>
            </b-col>
            <b-col md="6" class="order-actions text-center text-lg-left">
              <b-button
                variant="outline-secondary" @click="startCancel"
                class="wide">Cancel Order</b-button>
            </b-col>
          </b-row>
          <div v-else class="close-button">
            <span @click="mode = null"><i class="fas fa-times"/></span>
          </div>
          <PaymentRow v-if="showPayment" class="mr-3"/>
          <b-row v-if="showCancelDetails" class="mr-3">
            <b-col md="7" class="pb-3">
              <b-textarea rows="4" v-model="reason" placeholder="Enter reason for cancelling"></b-textarea>
            </b-col>
            <b-col md="3" class="text-right mb-3">
              <ActionButton
                  action="cancelOrder" :arg="reason"
                  variant="outline-secondary"
                  success="Order has been Cancelled">Cancel Order</ActionButton>
            </b-col>
          </b-row>
        </div>
        <LineDetailRow
          class="order-line"
          v-for="line in orderDetails.lines"
          :key="line.url"
          :order="orderDetails"
          :line="line"
          :can-delete="canDeleteLines"
          :can-change="canChangeLines"
          />
      </article>
    </MessageAlert>
  </b-container>
</template>

<style scoped lang="scss">
@import '~scss/abstracts/_variables.scss';
@import '~scss/colors.scss';

.close-button {
  position: relative;
  span {
    position: absolute;
    right: 0;
    z-index: 10;
  }
}

.order-header {
  margin-top: 1em;
  margin-bottom: 1em;
  border: 1px solid $grey;
  color: $light-charcoal;
  background-color: $light-grey;
  border-radius: 0.25rem;
  padding: 1em 1em 1em 1em;
}
.order-line {
  border-top: 1px solid $gray-0;
  padding: 0.5em 0;
}
.wide {
  width: 100%;
}

.embedded-input-container {
  position: relative;
  padding: 5px 0 10px 5px;

  .embedded-input {
    border: 1px solid $gray-0;
    border-radius: 0;
    margin: -3px -6px;
    padding: 2px 4px;
    line-height: 13px;
    width: 15em;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 2px;
  }

  span {
    position: absolute;
    top: 6px;

    &.ok {
      color: $alert-green;
      right: 16px;
    }

    &.cancel {
      color: $alert-red;
      right: 0;
    }
  }
}

@media screen and (max-width: 767.98px) {
  .wide {
    display: block;
  }
}
</style>

<script>
import { mapActions, mapGetters } from 'vuex';
import LineDetailRow from "./LineDetailRow";
import PaymentRow from "./PaymentRow";
import notifyPromise from '@/lib/bubble/notifyPromise';

export default {
  components: {LineDetailRow, PaymentRow},
  props: ['id'],
  data() {
    return {
      mode: null,
      reason: '',
      isAddVoucherFormShown: false,
      voucherCode: null,
    };
  },
  methods: {
    ...mapActions(['loadOrderDetails', 'reloadOrderDetails']),
    startPayment() {
      this.mode = 'paying';
    },
    startCancel() {
      this.mode = 'cancelling';
    },
    hideVoucherForm() {
      this.isAddVoucherFormShown = false;
      this.voucherCode = null;
    },
    async applyDiscount(evt) {
      if (this.voucherCode) {
        const messageSuccess = 'Discount has been applied';
        await notifyPromise(evt.path, this.$store.dispatch('addOrderDiscount', this.voucherCode), messageSuccess);
      }
    },
  },
  async mounted() {
    await this.loadOrderDetails(this.id);
  },
  computed: {
    ...mapGetters(['orderDetails', 'flags']),
    canDeleteLines() {
      return this.orderDetails.allowed_changes.includes('delete_line') && this.orderDetails.lines.length > 1;
    },
    canChangeLines() {
      return this.orderDetails.allowed_changes.includes('change_line');
    },
    canChangeStatus() {
      return this.orderDetails.allowed_changes.includes('change_status');
    },
    showButtons() {
      return !this.mode && this.canChangeStatus && !this.isStaffDashboard;
    },
    showPayment() {
      return this.mode === 'paying' && this.canChangeStatus && !this.isStaffDashboard;
    },
    showCancelDetails() {
      return this.mode === 'cancelling' && this.canChangeStatus && !this.isStaffDashboard;
    },
    isStaffDashboard() {
      return this.$route.path.startsWith('/admin');
    },
    hasVoucherDiscount() {
      return this.orderDetails.voucher_discounts.length > 0;
    },
    voucherCodeToShow() {
      if (this.hasVoucherDiscount) {
        const voucherDiscountFirst = this.orderDetails.voucher_discounts[0]
        return voucherDiscountFirst.voucher.code;
      }
      return '(none)';
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/pages/dashboard/orders/OrderDetail.vue